// src/utils/cloudinaryUpload.js
// Utilitaire d'upload direct vers Cloudinary (depuis le navigateur)
// Remplace l'upload PHP vers le serveur local
//
// ============================================================
// CONFIGURATION — à remplir après avoir créé votre compte :
// https://cloudinary.com/users/register/free
// ============================================================

const CLOUDINARY_CLOUD_NAME =
  process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'ukk1sgcf';

// Upload Preset : créez-en un UNSIGNED dans Cloudflare Dashboard
// Cloudinary Dashboard > Settings > Upload > Upload presets > Add upload preset
// → Signing Mode : "Unsigned"
// → Preset name : "efis_uploads"
const CLOUDINARY_UPLOAD_PRESET =
  process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'efis_uploads';

/**
 * Uploade un fichier image directement vers Cloudinary.
 * @param {File} file - Le fichier à uploader
 * @param {Function} onProgress - Callback optionnel (progress 0-100)
 * @returns {Promise<{success: boolean, data: {url: string}, message?: string}>}
 */
export async function uploadToCloudinary(file, onProgress = null) {
  // Vérification du type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return {
      success: false,
      message: 'Type de fichier invalide. Seuls JPG, PNG, GIF et WEBP sont acceptés.'
    };
  }

  // Vérification de la taille (max 10MB)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      success: false,
      message: 'Fichier trop grand. Maximum 10MB.'
    };
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', 'efis'); // Dossier dans Cloudinary

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        message: errorData.error?.message || 'Erreur lors de l\'upload'
      };
    }

    const data = await response.json();

    // Cloudinary retourne secure_url (HTTPS)
    return {
      success: true,
      data: {
        url: data.secure_url,
        public_id: data.public_id,
        width: data.width,
        height: data.height
      }
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    return {
      success: false,
      message: 'Erreur réseau lors de l\'upload. Vérifiez votre connexion.'
    };
  }
}

/**
 * Uploade plusieurs fichiers en parallèle vers Cloudinary.
 * @param {FileList|File[]} files
 * @returns {Promise<string[]>} - Liste des URLs uploadées avec succès
 */
export async function uploadMultipleToCloudinary(files) {
  const uploadPromises = Array.from(files).map(file => uploadToCloudinary(file));
  const results = await Promise.all(uploadPromises);
  return results
    .filter(r => r.success)
    .map(r => r.data.url);
}
